import { useEffect, useMemo, useState } from "react";

import { useClientManager } from "@megaron/react-clients";
import { TicketDetailDto } from "@megaron/support-contracts";

export type ContactOption = {
  label: string;
  value: string;
  email: string;
  name: string;
};

function uniqueEmails(emails: (string | null | undefined)[]): string[] {
  const values: string[] = [];
  for (const email of emails) {
    const normalized = (email ?? "").trim().toLowerCase();
    if (!normalized) continue;
    if (!values.includes(normalized)) values.push(normalized);
  }
  return values;
}

type Params = {
  ticket: TicketDetailDto;
  isOpen: boolean;
};

type Result = {
  contactOptions: ContactOption[];
  reporterEmailOptions: Array<{ label: string; value: string }>;
  selectedContact: string;
  reporterEmail: string;
  reporterName: string;
  onContactChange: (value: string) => void;
  onReporterEmailChange: (value: string) => void;
};

export const useTicketContactOptions = ({ ticket, isOpen }: Params): Result => {
  const crm = useClientManager("crm");
  const [selectedContact, setSelectedContact] = useState("");
  const [reporterEmail, setReporterEmail] = useState(ticket.reporterEmail);
  const [reporterName, setReporterName] = useState(ticket.reporterName);

  const customerQuery = crm
    .customerDocBulkQuery()
    .useQuery(
      { customerUuids: ticket.crmCustomerUuid ? [ticket.crmCustomerUuid] : [] },
      { enabled: isOpen && !!ticket.crmCustomerUuid },
    );
  const contactsQuery = crm
    .customerContactsQuery()
    .useQuery(
      { customerId: ticket.crmCustomerUuid ?? ticket.id },
      { enabled: isOpen && !!ticket.crmCustomerUuid },
    );

  const reporterEmailOptions = useMemo(() => {
    const crmCustomerEmail = customerQuery.data?.[0]?.email ?? undefined;
    const contactEmails = (contactsQuery.data ?? []).map((c) => c.email);
    const emails = uniqueEmails([crmCustomerEmail, ...contactEmails, ticket.reporterEmail]);
    return emails.map((email) => ({ label: email, value: email }));
  }, [contactsQuery.data, customerQuery.data, ticket.reporterEmail]);

  const contactOptions = useMemo((): ContactOption[] => {
    const options: ContactOption[] = [];
    const currentEmail = ticket.reporterEmail.trim().toLowerCase();
    const currentName = ticket.reporterName.trim() || currentEmail.split("@")[0];
    if (currentEmail) {
      options.push({ label: `${currentName} (${currentEmail})`, value: `${currentEmail}|${currentName}`, email: currentEmail, name: currentName });
    }
    const customer = customerQuery.data?.[0];
    const customerEmail = customer?.email?.trim().toLowerCase() ?? "";
    const customerName = customer?.name?.trim() ?? "";
    if (customerEmail && customerName) {
      options.push({ label: `${customerName} (${customerEmail})`, value: `${customerEmail}|${customerName}`, email: customerEmail, name: customerName });
    }
    for (const contact of contactsQuery.data ?? []) {
      const email = contact.email.trim().toLowerCase();
      const name = contact.name.trim();
      if (!email || !name) continue;
      options.push({ label: `${name} (${email})`, value: `${email}|${name}`, email, name });
    }
    const deduplicated: ContactOption[] = [];
    for (const option of options) {
      if (!deduplicated.some((item) => item.value === option.value)) deduplicated.push(option);
    }
    return deduplicated;
  }, [contactsQuery.data, customerQuery.data, ticket.reporterEmail, ticket.reporterName]);

  useEffect(() => {
    if (!isOpen) {
      setReporterName(ticket.reporterName);
      setReporterEmail(ticket.reporterEmail);
      setSelectedContact("");
    }
  }, [isOpen, ticket.reporterEmail, ticket.reporterName]);

  useEffect(() => {
    if (!isOpen || reporterEmailOptions.length === 0) return;
    const hasActive = reporterEmailOptions.some((o) => o.value === reporterEmail);
    if (!hasActive) setReporterEmail(reporterEmailOptions[0].value);
  }, [isOpen, reporterEmail, reporterEmailOptions]);

  useEffect(() => {
    if (!isOpen || contactOptions.length === 0) return;
    const currentValue = `${reporterEmail.trim().toLowerCase()}|${reporterName.trim()}`;
    const matching = contactOptions.find((o) => o.value === currentValue);
    if (matching) { setSelectedContact(matching.value); return; }
    const byEmail = contactOptions.find((o) => o.email === reporterEmail.trim().toLowerCase());
    if (byEmail) { setSelectedContact(byEmail.value); setReporterName(byEmail.name); return; }
    const first = contactOptions[0];
    setSelectedContact(first.value);
    setReporterEmail(first.email);
    setReporterName(first.name);
  }, [contactOptions, isOpen, reporterEmail, reporterName]);

  const onContactChange = (value: string) => {
    const selected = contactOptions.find((o) => o.value === value);
    if (!selected) return;
    setSelectedContact(selected.value);
    setReporterEmail(selected.email);
    setReporterName(selected.name);
  };

  const onReporterEmailChange = (value: string) => {
    setReporterEmail(value);
    const byEmail = contactOptions.find((o) => o.email === value);
    if (byEmail) { setSelectedContact(byEmail.value); setReporterName(byEmail.name); }
  };

  return { contactOptions, reporterEmailOptions, selectedContact, reporterEmail, reporterName, onContactChange, onReporterEmailChange };
};
